import { styled, useTheme } from '@mui/material';

import { getThemeColor } from '../../../utils/theme';

import { tokens } from '../../../theme/generated/tokens';
import { Sizes } from '../../../types';

export type DotsLoaderSize = Sizes<'sm' | 'md' | 'lg'>;

const dotSizePx: Record<DotsLoaderSize, number> = {
  sm: 6,
  md: 8,
  lg: 12,
};

type DotsLoaderProps = {
  size?: DotsLoaderSize;
  color?: string;
};

const Root = styled('div', {
  name: 'DsDotsLoader',
  slot: 'Root',
  target: 'DsDotsLoader-Root',
  overridesResolver: (_, styles) => [styles.root],
})<{ ownerState: { sizePx: number; color?: string } }>(({ ownerState }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,

  '@keyframes loader': {
    to: {
      opacity: 0.1,
    },
  },

  '.DsDotsLoader-Dot': {
    background: ownerState.color,
    width: ownerState.sizePx,
    height: ownerState.sizePx,
    animation: 'loader 0.6s infinite alternate',

    '&:nth-child(1)': {
      animationDelay: '0.0s',
    },

    '&:nth-child(2)': {
      animationDelay: '0.2s',
    },

    '&:nth-child(3)': {
      animationDelay: '0.4s',
    },
  },
}));

const Dot = styled('div', {
  name: 'DsDotsLoader',
  slot: 'Dot',
  target: 'DsDotsLoader-Dot',
  overridesResolver: (_, styles) => [styles.dot],
})({
  display: 'block',
  borderRadius: tokens.theme.radius.full,
});

export const DotsLoader = ({
  size = 'sm',
  color = 'grey.500',
}: DotsLoaderProps) => {
  const theme = useTheme();
  const ownerState = {
    sizePx: dotSizePx[size],
    color: getThemeColor(theme, color),
  };
  return (
    <Root ownerState={ownerState}>
      <Dot />
      <Dot />
      <Dot />
    </Root>
  );
};
